import {
  type RecordJobData,
  type InputData,
  type OutputData,
  unwrapValueData,
  type ValueData,
  type ValueData_V,
} from "@smartsuite/automation-engine-api";
import { translateIfPresent } from "../../../../../locales/i18n";
import {
  valueDataToString,
  valueDataToStrings,
} from "@smartsuite/automation-conditions";
import {
  OUTPUT_APPLICATION_ID,
  RECORD_ID,
} from "../../../../../utils/constants";

const getTypeString = (value: ValueData_V): string => {
  let type: string;
  if (value.value.oneofKind === "string") {
    type = "Text";
  } else if (value.value.oneofKind === "number") {
    type = "Number";
  } else if (value.value.oneofKind === "bool") {
    type = "Boolean";
  } else {
    type = String(value.value.oneofKind);
  }
  type = type.charAt(0).toUpperCase() + type.slice(1);

  return translateIfPresent(`caption.${type}`, type);
};
const getResponseContentInputString = (value?: ValueData): string => {
  if (value === undefined) {
    return "";
  }
  value = unwrapValueData(value);
  return value.values
    .flatMap((v) => {
      if (v.value.oneofKind === "keyValue") {
        const key = valueDataToString(v.value.keyValue.key);
        const values = getResponseContentInputString(v.value.keyValue.value);
        return `[${key}]: ${values}`;
      } else if (v.value.oneofKind === "list") {
        const listValues = v.value.list.values
          .flatMap((vl) => getResponseContentInputString(vl))
          .join(",");
        const str = listValues.length > 0 ? `List: ${listValues}` : "List";
        return translateIfPresent(`caption.${str}`, str);
      } else {
        return getTypeString(v);
      }
    })
    .join("; ");
};

const INPUTS_SORT_ORDER = [
  "request-url",
  "http-method",
  "request-headers",
  "request-url-params",
  "request-content",
  "response-content",
  "response-status-action",
];

interface GenerateWebhookGroup {
  getInputValue: (input: InputData) => string;
  getSortedInputs: (recordJob: RecordJobData) => InputData[];
  getOutputDatas: (recordJob?: RecordJobData) => OutputData[];
}

export const useGenerateWebhookGroup = (
  timezone?: string,
): GenerateWebhookGroup => {
  const getInputValue = (input: InputData): string => {
    if (input.value === undefined) {
      return "";
    }
    if (input.inputId === "response-content") {
      return getResponseContentInputString(input.value);
    } else {
      return valueDataToStrings(input.value, timezone).join("; ");
    }
  };

  const getSortedInputs = (recordJob: RecordJobData): InputData[] => {
    return recordJob.inputs
      .filter(
        (input) => ![RECORD_ID, OUTPUT_APPLICATION_ID].includes(input.inputId),
      )
      .sort((a, b) => {
        const indexA = INPUTS_SORT_ORDER.indexOf(a.inputId);
        const indexB = INPUTS_SORT_ORDER.indexOf(b.inputId);

        if (indexA === -1 && indexB === -1) {
          return 0;
        }
        if (indexA === -1) {
          return 1;
        }
        if (indexB === -1) {
          return -1;
        }
        return indexA - indexB;
      });
  };

  const getOutputDatas = (recordJob?: RecordJobData): OutputData[] => {
    return [...(recordJob?.outputData?.outputs ?? [])].sort((a, b) =>
      b.outputId === "response-raw-body" ? -1 : 1,
    );
  };

  return { getInputValue, getSortedInputs, getOutputDatas };
};
